import React, { useEffect, useMemo, useState } from 'react';
import { SLIDER } from '@/constants/homepage';
import { useInterval } from '@/hooks';
import { SlideModel } from '@/models';
import styles from './CarouselDesktop.module.scss';

type CarouselDesktopProps = {
	onClickSlide: (content: any, key: number) => void;
	slides?: SlideModel[];
};

export const CarouselDesktop = ({ onClickSlide, slides }: CarouselDesktopProps): React.JSX.Element => {
	const [index, setIndex] = useState(0);
	const [isClicking, setIsClicking] = useState(false);
	const [slidesToRender, setSlidesToRender] = useState<SlideModel[]>([]);

	useEffect(() => {
		if (!slides || slides?.length === 0) {
			return;
		}
		return slides?.length > 1 ? setSlidesToRender([...slides, slides[0]]) : setSlidesToRender([...slides]);
	}, [slides]);

	const nbSlideToRender = useMemo(() => slidesToRender.length, [slidesToRender]);

	const handleClick = (key: number) => {
		setIndex(key);
		setIsClicking(true);
		setTimeout(() => {
			setIsClicking(false);
		}, SLIDER.TRANSITION_DELAY);
	};

	useInterval(() => {
		setIndex((index + 1) % nbSlideToRender);
	}, SLIDER.SLIDE_INTERVAL);

	useEffect(() => {
		if (index === nbSlideToRender - 1) {
			setTimeout(() => {
				setIndex(0);
			}, SLIDER.TRANSITION_DELAY);
		}
	}, [index]);

	return nbSlideToRender > 0 ? (
		<div className={styles.container} data-rbf="carousel-desktop-container" data-test-id="carousel-desktop-container">
			<div
				className={styles.slides}
				style={{
					transform: `translateX(-${index * 100}%)`,
					transition: index === 0 && !isClicking ? undefined : 'transform 1.5s',
				}}
			>
				{slidesToRender.map((slide, index) => (
					<div
						key={index}
						onClick={() => onClickSlide(slide, index)}
						className={styles.slide}
						data-rbf="carousel-desktop-slides"
						data-test-id="carousel-desktop-slides"
					>
						<a href={slide.urlDest} aria-label={slide.alt}>
							<picture>
								<source srcSet={slide.imgXl} media="(min-width: 1441px)" />
								<source srcSet={slide.imgL} media="(min-width: 769px)" />
								<source srcSet={slide.imgM} media="(min-width: 480px)" />
								<img
									src={slide.imgXl}
									alt={slide.alt}
									loading={index > 0 ? 'lazy' : undefined}
									fetchPriority={index === 0 ? 'high' : undefined}
									decoding={index === 0 ? undefined : 'async'}
									data-rbf="carousel-desktop-slides-img"
								/>
							</picture>
						</a>
					</div>
				))}
			</div>
			<div className={styles['doted-container']}>
				{slidesToRender.map((_, i) => {
					return (
						i < nbSlideToRender - 1 && (
							<span
								key={i}
								onClick={() => handleClick(i)}
								className={`${styles.dot} ${index % (nbSlideToRender - 1) === i ? styles.active : ''}`}
							/>
						)
					);
				})}
			</div>
		</div>
	) : null;
};

export default CarouselDesktop;
